<template>
  <div
    class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-blueGray-200"
  >
    <div class="rounded-t mb-0 px-4 py-3 border-0 bg-emerald-500">
      <div class="flex flex-wrap items-center justify-between">
        <h3 class="font-semibold text-lg text-black">MY INVOICES</h3>
        <button
          @click="openUploadModal"
          class="bg-white text-emerald-600 text-sm font-bold px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150"
        >
          <i class="fas fa-upload mr-1"></i> Upload Invoice
        </button>
      </div>
    </div>

    <!-- Filter / Sort Bar -->
    <div class="flex flex-wrap items-center gap-2 px-4 py-2 bg-blueGray-100 border-b">
      <label class="text-xs font-bold uppercase text-blueGray-500">From:</label>
      <input type="date" v-model="filterDateFrom" class="border rounded px-2 py-1 text-sm" />
      <label class="text-xs font-bold uppercase text-blueGray-500">To:</label>
      <input type="date" v-model="filterDateTo" class="border rounded px-2 py-1 text-sm" />
      <label class="text-xs font-bold uppercase text-blueGray-500 ml-2">Sort:</label>
      <select v-model="sortOrder" class="border rounded px-2 py-1 text-sm">
        <option value="latest">Latest First</option>
        <option value="oldest">Oldest First</option>
      </select>
    </div>

    <div class="block w-full overflow-x-auto">
      <table class="items-center w-full bg-transparent border-collapse">
        <thead>
          <tr>
            <th class="text-white px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-600">
              Invoice Date
            </th>
            <th class="text-white px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-600">
              Client
            </th>
            <th class="text-white px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-600">
              Description
            </th>
            <th class="text-white px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center bg-blueGray-600">
              Amount
            </th>
            <th class="text-white px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center bg-blueGray-600">
              Invoice File
            </th>
            <th class="text-white px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center bg-blueGray-600">
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="filteredInvoices.length === 0">
            <td
              colspan="6"
              class="px-6 py-10 text-center text-blueGray-500 font-semibold text-sm"
            >
              No invoices submitted yet.
            </td>
          </tr>
          <tr
            v-for="invoice in filteredInvoices"
            :key="invoice.id"
            class="border-b border-gray-300"
          >
            <td class="px-6 align-middle py-3 text-sm whitespace-nowrap font-semibold text-left">
              {{ formatToYmd(invoice.date_purchased) }}
            </td>
            <td class="px-6 align-middle py-3 text-sm whitespace-nowrap font-semibold text-left">
              {{ invoice.client_name || "—" }}
            </td>
            <td class="px-6 align-middle py-3 text-sm whitespace-nowrap font-semibold text-left">
              {{ invoice.description }}
            </td>
            <td class="px-6 align-middle py-3 text-sm whitespace-nowrap font-semibold text-center">
              {{ formattedPrice(invoice.invoice_price) }}
            </td>
            <td class="px-6 align-middle py-3 text-sm whitespace-nowrap text-center">
              <div v-if="invoice.invoice_file" class="flex justify-center gap-1">
                <button
                  @click="openInvoiceFilePreview(invoice.id)"
                  class="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded shadow hover:shadow-md"
                  title="View Invoice"
                >
                  <i class="fas fa-eye mr-1"></i> View
                </button>
                <button
                  @click="downloadInvoiceFile(invoice.id)"
                  class="bg-blueGray-600 text-white text-xs font-bold px-3 py-1 rounded shadow hover:shadow-md"
                  title="Download Invoice"
                >
                  <i class="fas fa-download mr-1"></i> Download
                </button>
              </div>
              <span v-else class="text-blueGray-400 text-xs">No file</span>
            </td>
            <td class="px-6 align-middle py-3 text-sm whitespace-nowrap font-semibold text-center">
              <span v-if="invoice.is_client_approved == 1" class="text-green-600">Approved</span>
              <span v-else-if="invoice.is_client_approved == -1" class="text-red-600">Declined</span>
              <span v-else class="text-yellow-600">Pending</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Upload Invoice Modal -->
    <div v-if="isUploadModalOpen" class="modal-overlay" @click.self="closeUploadModal">
      <div class="modal-container">
        <h3 class="text-lg font-bold text-blueGray-700 mb-4">Upload Invoice</h3>

        <div class="mb-3">
          <label class="block text-xs font-bold uppercase text-blueGray-600 mb-2">
            Client / Program Name
          </label>
          <input
            v-model="formData.client_name"
            type="text"
            placeholder="Who is this invoice for?"
            class="border rounded w-full px-3 py-2 text-sm text-blueGray-700 focus:outline-none focus:ring"
          />
        </div>

        <div class="mb-3">
          <label class="block text-xs font-bold uppercase text-blueGray-600 mb-2">
            Invoice Date <span class="text-red-500">*</span>
          </label>
          <input
            v-model="formData.date_purchased"
            type="date"
            class="border rounded w-full px-3 py-2 text-sm text-blueGray-700 focus:outline-none focus:ring"
          />
          <span v-if="validation_fail && !formData.date_purchased" class="text-red-500 text-xs">
            Invoice date is required
          </span>
        </div>

        <div class="mb-3">
          <label class="block text-xs font-bold uppercase text-blueGray-600 mb-2">
            Amount <span class="text-red-500">*</span>
          </label>
          <input
            v-model="formData.invoice_price"
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            class="border rounded w-full px-3 py-2 text-sm text-blueGray-700 focus:outline-none focus:ring"
          />
          <span v-if="validation_fail && !formData.invoice_price" class="text-red-500 text-xs">
            Amount is required
          </span>
        </div>

        <div class="mb-3">
          <label class="block text-xs font-bold uppercase text-blueGray-600 mb-2">
            Description <span class="text-red-500">*</span>
          </label>
          <textarea
            v-model="formData.description"
            rows="3"
            placeholder="Describe the invoice..."
            class="border rounded w-full px-3 py-2 text-sm text-blueGray-700 focus:outline-none focus:ring resize-none"
          ></textarea>
          <span v-if="validation_fail && !formData.description.trim()" class="text-red-500 text-xs">
            Description is required
          </span>
        </div>

        <div class="mb-4">
          <label class="block text-xs font-bold uppercase text-blueGray-600 mb-2">
            Invoice File <span class="text-red-500">*</span>
          </label>
          <input
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xlsx"
            class="border rounded w-full px-3 py-2 text-sm text-blueGray-700 focus:outline-none focus:ring"
            @change="handleFileChange"
          />
          <span v-if="validation_fail && !formData.invoice_file" class="text-red-500 text-xs">
            Please attach the invoice file
          </span>
        </div>

        <div class="flex justify-end gap-2">
          <button
            @click="closeUploadModal"
            class="bg-blueGray-500 text-black text-xs font-bold uppercase px-4 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150"
          >
            Cancel
          </button>
          <button
            @click="submitInvoice"
            :disabled="isSubmitting"
            class="bg-emerald-600 text-white text-xs font-bold uppercase px-4 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150 disabled:opacity-50"
          >
            {{ isSubmitting ? "Uploading..." : "Submit Invoice" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2/dist/sweetalert2.js";
import "sweetalert2/dist/sweetalert2.min.css";

export default {
  data() {
    return {
      invoices: [],
      sortOrder: "latest",
      filterDateFrom: "",
      filterDateTo: "",
      isUploadModalOpen: false,
      isSubmitting: false,
      validation_fail: false,
      formData: {
        client_name: "",
        date_purchased: "",
        invoice_price: "",
        description: "",
        invoice_file: null,
      },
    };
  },
  computed: {
    filteredInvoices() {
      let list = [...this.invoices];
      if (this.filterDateFrom) {
        list = list.filter((i) => i.date_purchased >= this.filterDateFrom);
      }
      if (this.filterDateTo) {
        list = list.filter((i) => i.date_purchased <= this.filterDateTo + "T23:59:59");
      }
      list.sort((a, b) => {
        const da = new Date(a.date_purchased);
        const db = new Date(b.date_purchased);
        return this.sortOrder === "latest" ? db - da : da - db;
      });
      return list;
    },
    formIsValid() {
      return (
        this.formData.date_purchased !== "" &&
        this.formData.invoice_price !== "" &&
        this.formData.description.trim() !== "" &&
        !!this.formData.invoice_file
      );
    },
  },
  methods: {
    async fetchInvoices() {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.post(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/vendor_fetch_invoices`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        this.invoices = response.data.data || [];
      } catch (error) {
        console.error("Error fetching invoices:", error);
        if (error.response && error.response.status === 401) {
          Swal.fire({
            icon: "error",
            title: "Unauthorized",
            text: "Your session has expired. Please log in again.",
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to load invoices. Please try again.",
          });
        }
      }
    },
    openUploadModal() {
      this.formData = {
        client_name: "",
        date_purchased: "",
        invoice_price: "",
        description: "",
        invoice_file: null,
      };
      this.validation_fail = false;
      this.isUploadModalOpen = true;
    },
    closeUploadModal() {
      this.isUploadModalOpen = false;
    },
    handleFileChange(event) {
      this.formData.invoice_file = event.target.files[0] || null;
    },
    async submitInvoice() {
      if (!this.formIsValid) {
        this.validation_fail = true;
        return;
      }

      this.isSubmitting = true;
      try {
        const token = localStorage.getItem("token");
        const payload = new FormData();
        payload.append("date_purchased", this.formData.date_purchased);
        payload.append("invoice_price", this.formData.invoice_price);
        payload.append("description", this.formData.description.trim());
        if (this.formData.client_name.trim()) {
          payload.append("client_name", this.formData.client_name.trim());
        }
        payload.append("invoice_file", this.formData.invoice_file);

        const response = await axios.post(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/vendor_add_invoice`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.status === 200) {
          Swal.fire({
            icon: "success",
            title: "Invoice Submitted",
            text: response.data.message || "Your invoice has been submitted.",
          });
          this.closeUploadModal();
          this.fetchInvoices();
        } else {
          Swal.fire({
            icon: "error",
            title: "Upload Failed",
            text: response.data.message || "An error occurred while uploading the invoice.",
          });
        }
      } catch (error) {
        console.error("Error uploading invoice:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            (error.response && error.response.data && error.response.data.message) ||
            "Failed to upload the invoice. Please try again.",
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    formatToYmd(dateTimeString) {
      if (!dateTimeString) return "—";
      const date = new Date(dateTimeString);
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const day = String(date.getUTCDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    formattedPrice(value) {
      if (!value) return "$0.00";
      const num = parseFloat(value);
      if (isNaN(num)) return "$0.00";
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num);
    },
    async openInvoiceFilePreview(invoiceId) {
      try {
        const token = localStorage.getItem("token");
        const { data } = await axios.post(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/invoice-file-signed-url`,
          {
            invoice_id: invoiceId,
            file_type: "invoice",
            receipt_index: 0,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        this.openFileSwal(
          data.signed_url,
          data.filename,
          data.allow_google_docs_viewer,
          data.download_url || data.signed_url,
          data.office_embed_source_url || null
        );
      } catch (error) {
        console.error(error);
        Swal.fire({
          icon: "error",
          title: "Could not open file",
          text:
            (error.response && error.response.data && error.response.data.message) ||
            "Please try again.",
        });
      }
    },
    async downloadInvoiceFile(invoiceId) {
      try {
        const token = localStorage.getItem("token");
        const { data } = await axios.post(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/invoice-file-signed-url`,
          { invoice_id: invoiceId, file_type: "invoice", receipt_index: 0 },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const url = data.download_url || data.signed_url;
        if (url) {
          window.open(url, "_blank", "noopener,noreferrer");
        }
      } catch (error) {
        console.error(error);
        Swal.fire({
          icon: "error",
          title: "Download Failed",
          text: "Could not download the file. Please try again.",
        });
      }
    },
    openFileSwal(previewUrl, filename, allowGoogleDocsViewer = false, downloadUrl = null, officeEmbedSourceUrl = null) {
      const dl = downloadUrl || previewUrl;
      Swal.fire({
        title: filename,
        html: this.getHtmlContent(previewUrl, filename, allowGoogleDocsViewer, dl, officeEmbedSourceUrl),
        showCloseButton: true,
        showCancelButton: false,
        showConfirmButton: false,
        focusConfirm: false,
        didOpen: (popup) => {
          const btn = popup.querySelector(".btn_invoice_modal_download");
          if (!btn) return;
          btn.addEventListener(
            "click",
            (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (dl) {
                window.open(dl, "_blank", "noopener,noreferrer");
              }
            },
            { once: true }
          );
        },
      });
    },
    // eslint-disable-next-line no-unused-vars
    getHtmlContent(previewUrl, file_name, allowGoogleDocsViewer = false, downloadUrl = null, officeEmbedSourceUrl = null) {
      const srcEsc = (u) => String(u).replace(/&/g, "&amp;").replace(/"/g, "&quot;");
      let content = "";
      const btn_class =
        "bg-lightBlue-600 text-white text-sm font-bold uppercase px-6 py-3 rounded shadow outline-none focus:outline-none mr-1 mb-1 w-half ease-linear transition-all duration-150 btn_invoice_modal_download";
      if (this.isImage(file_name)) {
        content += `<img src="${srcEsc(previewUrl)}" style="max-width: 100%; max-height: 100%;">`;
        content += `<br><button type="button" class="${btn_class}">Download</button>`;
      } else if (this.isPdf(file_name)) {
        content += `<iframe src="${srcEsc(previewUrl)}" style="width:100%; height:70vh; border:0;" title="PDF Preview"></iframe>`;
        content += `<br><button type="button" class="${btn_class}">Download</button>`;
      } else if (this.isWord(file_name)) {
        if (allowGoogleDocsViewer) {
          const docSrc = officeEmbedSourceUrl || previewUrl;
          const embedUrl = this.getOfficeWebViewerUrl(docSrc);
          content += `<iframe src="${srcEsc(embedUrl)}" style="width:100%; height:70vh; border:0;" title="Document Preview" allowfullscreen></iframe>`;
        } else {
          content += `<p class="text-sm text-left">Word documents cannot be previewed in the browser. Please download the file.</p>`;
        }
        content += `<br><button type="button" class="${btn_class}">Download</button>`;
      } else if (this.isExcel(file_name)) {
        content += `<p class="text-sm text-left">Please download the file to view.</p>`;
        content += `<br><button type="button" class="${btn_class}">Download</button>`;
      } else {
        content += "Unsupported file type";
      }
      return content;
    },
    isImage(fileName) {
      return [".png", ".jpg", ".jpeg", ".gif", ".bmp"].some((ext) =>
        fileName.toLowerCase().endsWith(ext)
      );
    },
    isPdf(fileName) {
      return fileName.toLowerCase().endsWith(".pdf");
    },
    isWord(fileName) {
      const v = fileName.toLowerCase();
      return v.endsWith(".doc") || v.endsWith(".docx");
    },
    isExcel(fileName) {
      return fileName.toLowerCase().endsWith(".xlsx");
    },
    getOfficeWebViewerUrl(fileUrl) {
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
    },
  },
  mounted() {
    this.fetchInvoices();
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1040;
}

.modal-container {
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
  z-index: 1050;
  width: 90%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
}
</style>
